import { useCallback, useEffect, useMemo, useRef } from 'react';
import { applyEdgeChanges, useEdgesState, useNodesState, useReactFlow } from '@xyflow/react';
import useFlowHandlers from './useFlowHandlers';
import useProximityConnect from './useProximityConnect';
import { createInitialGraphDFS } from '../utils/GraphLayout';
import {
  applyStoredLayout,
  saveFlowLayout,
} from '../utils/workflowFlowLayoutStorage';
import {
  syncFlowGraphFromSteps,
  deriveEdgesFromWorkflowSteps,
  mergeDerivedEdgesWithTemp,
  countNavigationActions,
} from '../utils/syncFlowGraphFromSteps';
import { getExistingNodeIds, getNewNodeId } from '../utils/NodeHelpers';
import { buildFlowNode } from '../utils/flowNodeData';
import { createWorkflowStep } from '../utils/workflowStepFactory';
import { WORKFLOW_NODE_DRAG_TYPE } from '../utils/workflowNodeTypes';
import { parseEdgeId, WORKFLOW_EDGE_TYPE } from '../utils/flowEdgeUtils';
import {
  appendNavigationAction,
  buildEdgeDeletionList,
  removeNavigationActions,
  removeStepsById,
} from '../utils/workflowGraphMutations';
import WorkflowStepNode from '../components/workflows/WorkflowStepNode';
import WorkflowNavigationEdge from '../components/workflows/WorkflowNavigationEdge';
import { IS_DEVELOPMENT } from '../../config/env';

export default function useFlowGraphEditor({
  workflow,
  setWorkflow,
  saveWorkflow,
  showDetailedContent,
  selectedAddNodeType,
  selectedItem,
  setSelectedItem,
  lastClickedNodeRef,
  editorSidebarRef,
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);

  const { screenToFlowPosition, getNodes, fitView } = useReactFlow();

  const workflowRef = useRef(workflow);
  workflowRef.current = workflow;

  const workflowStepsKey = useMemo(
    () => (Array.isArray(workflow?.workflow_steps) ? JSON.stringify(workflow.workflow_steps) : ''),
    [workflow?.workflow_steps]
  );

  const defaultEdgeOptions = useMemo(
    () => ({
      type: WORKFLOW_EDGE_TYPE,
      labelShowBg: true,
      labelBgStyle: { fill: '#ffffff', fillOpacity: 0.92 },
      labelBgPadding: [4, 8],
      labelBgBorderRadius: 4,
      labelStyle: { fill: '#212529', fontSize: 12, fontWeight: 500 },
    }),
    []
  );

  const nodeTypes = useMemo(
    () => ({
      workflowStep: WorkflowStepNode,
    }),
    []
  );

  const edgeTypes = useMemo(
    () => ({
      [WORKFLOW_EDGE_TYPE]: WorkflowNavigationEdge,
    }),
    []
  );

  const handleEdgesChange = useCallback(
    (changes) => {
      setEdges((current) => applyEdgeChanges(changes, current));
    },
    [setEdges]
  );

  useEffect(() => {
    const steps = workflowRef.current?.workflow_steps;
    if (!Array.isArray(steps) || nodes.length === 0) return;

    setEdges((prev) => {
      const derived = deriveEdgesFromWorkflowSteps(steps);
      const nonTempPrev = prev.filter((edge) => edge.className !== 'temp');

      if (IS_DEVELOPMENT) {
        const expected = countNavigationActions(steps);
        if (nonTempPrev.length !== expected) {
          console.warn(
            '[FlowDiagram] Correcting edge/graph drift:',
            { edgesOnCanvas: nonTempPrev.length, navigationActions: expected }
          );
        }
      }

      return mergeDerivedEdgesWithTemp(derived, prev);
    });
  }, [workflowStepsKey, nodes.length, setEdges]);

  const schedulePersistLayout = useCallback(
    (workflowOverride) => {
      setTimeout(() => {
        const wf = workflowOverride ?? workflowRef.current;
        if (!wf?.id) return;
        const positions = {};
        getNodes().forEach((n) => {
          positions[n.id] = { ...n.position };
        });
        saveFlowLayout({
          rootId: wf.root_workflow_id ?? wf.id,
          workflowId: wf.id,
          version: wf.version,
          positionsById: positions,
          workflowSteps: wf.workflow_steps,
        });
      }, 0);
    },
    [getNodes]
  );

  const {
    handleConnect,
    handleConnectEnd,
    handleWorkflowChange,
  } = useFlowHandlers({
    workflow,
    setWorkflow,
    setNodes,
    screenToFlowPosition,
    getNewNodeId,
    getExistingNodeIds,
    showDetailedContent,
    selectedAddNodeType,
  });

  const handleProximityConnect = useCallback(
    (edge) => {
      setWorkflow((prev) => {
        if (!prev) return prev;
        return appendNavigationAction(prev, edge.source, edge.target);
      });
    },
    [setWorkflow]
  );

  const { onNodeDrag, onNodeDragStop: onProximityDragStop } = useProximityConnect({
    setEdges,
    onProximityConnect: handleProximityConnect,
    workflow,
  });

  const onNodesDelete = useCallback(
    (deletedNodes) => {
      if (!workflow) return;

      const updatedWorkflow = removeStepsById(
        workflow,
        deletedNodes.map((node) => node.id)
      );

      workflowRef.current = updatedWorkflow;
      setWorkflow(updatedWorkflow);

      if (selectedItem?.type === 'node' && deletedNodes.some((node) => node.id === selectedItem.id)) {
        setSelectedItem(null);
      }

      if (lastClickedNodeRef.current && deletedNodes.some((node) => node.id === lastClickedNodeRef.current)) {
        lastClickedNodeRef.current = null;
      }

      saveWorkflow(updatedWorkflow);
      schedulePersistLayout(updatedWorkflow);
    },
    [workflow, setWorkflow, selectedItem, setSelectedItem, saveWorkflow, schedulePersistLayout, lastClickedNodeRef]
  );

  const onEdgesDelete = useCallback(
    (deletedEdges) => {
      if (!workflow) return;

      const deletions = buildEdgeDeletionList(deletedEdges, parseEdgeId);
      const updatedWorkflow = removeNavigationActions(workflow, deletions);
      setWorkflow(updatedWorkflow);

      if (
        selectedItem?.type === 'edge' &&
        deletedEdges.some((edge) => edge.id === selectedItem.edgeId)
      ) {
        setSelectedItem(null);
      }

      saveWorkflow(updatedWorkflow);
    },
    [workflow, setWorkflow, selectedItem, setSelectedItem, saveWorkflow]
  );

  useEffect(() => {
    if (!workflow?.workflow_steps) return;
    if (nodes.length > 0) return;
    const { layoutNodes, layoutEdges } = createInitialGraphDFS(
      workflow.workflow_steps,
      showDetailedContent
    );
    setNodes(applyStoredLayout(layoutNodes, workflow));
    setEdges(layoutEdges);
  }, [workflow, nodes.length, setNodes, setEdges, showDetailedContent]);

  useEffect(() => {
    setNodes((prev) =>
      prev.map((node) => ({
        ...node,
        data: {
          ...node.data,
          showDetailedContent,
        },
      }))
    );
  }, [showDetailedContent, setNodes]);

  const addNodeAtPosition = useCallback(
    (nodeType, position) => {
      if (!workflow) return;

      const newId = getNewNodeId(getExistingNodeIds(workflow.workflow_steps));
      const newNode = createWorkflowStep(nodeType, newId);

      const updatedWorkflow = {
        ...workflow,
        workflow_steps: [...workflow.workflow_steps, newNode],
      };
      workflowRef.current = updatedWorkflow;
      setWorkflow(updatedWorkflow);

      setNodes((prev) => {
        const built = buildFlowNode(newNode, position, showDetailedContent);
        const next = [...prev, built];
        if (updatedWorkflow.id) {
          const positions = {};
          next.forEach((n) => {
            positions[n.id] = { ...n.position };
          });
          saveFlowLayout({
            rootId: updatedWorkflow.root_workflow_id ?? updatedWorkflow.id,
            workflowId: updatedWorkflow.id,
            version: updatedWorkflow.version,
            positionsById: positions,
            workflowSteps: updatedWorkflow.workflow_steps,
          });
        }
        return next;
      });
    },
    [workflow, setWorkflow, setNodes, showDetailedContent]
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      const nodeType = event.dataTransfer.getData(WORKFLOW_NODE_DRAG_TYPE);
      if (!nodeType || !workflow) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      addNodeAtPosition(nodeType, position);
    },
    [workflow, screenToFlowPosition, addNodeAtPosition]
  );

  const handleUploadJSON = useCallback(
    (jsonData) => {
      if (!workflow) return;

      const allowedFields = ['self_startable', 'workflow_steps'];
      const updatedWorkflow = { ...workflow };
      Object.keys(jsonData).forEach((key) => {
        if (allowedFields.includes(key)) {
          updatedWorkflow[key] = jsonData[key];
        }
      });

      setWorkflow(updatedWorkflow);
      syncFlowGraphFromSteps(updatedWorkflow, showDetailedContent, setNodes, setEdges);
    },
    [workflow, setWorkflow, showDetailedContent, setNodes, setEdges]
  );

  const handleAiStepsApplied = useCallback(
    (steps) => {
      if (!workflow) return;
      const updated = { ...workflow, workflow_steps: steps };
      setWorkflow(updated);
      syncFlowGraphFromSteps(updated, showDetailedContent, setNodes, setEdges);
      setSelectedItem(null);
      lastClickedNodeRef.current = null;
    },
    [workflow, setWorkflow, showDetailedContent, setNodes, setEdges, setSelectedItem, lastClickedNodeRef]
  );

  const handleAutoLayout = useCallback(() => {
    if (!workflow?.workflow_steps?.length) return;
    const { layoutNodes } = createInitialGraphDFS(workflow.workflow_steps, showDetailedContent);
    setNodes((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));
      return layoutNodes.map((ln) => {
        const existing = prevById.get(ln.id);
        return existing ? { ...existing, position: ln.position } : ln;
      });
    });
    schedulePersistLayout();
  }, [workflow, showDetailedContent, setNodes, schedulePersistLayout]);

  const onNodeDragStop = useCallback(
    (event, node, draggedNodes) => {
      onProximityDragStop(event, node, draggedNodes);
      schedulePersistLayout();
    },
    [onProximityDragStop, schedulePersistLayout]
  );

  const onNodeClick = useCallback(
    (_, node) => {
      if (lastClickedNodeRef.current === node.id) return;
      lastClickedNodeRef.current = node.id;
      setSelectedItem({ type: 'node', id: node.id });
    },
    [setSelectedItem, lastClickedNodeRef]
  );

  const onEdgeClick = useCallback(
    (_, edge) => {
      lastClickedNodeRef.current = null;
      const actionIndex = edge.data?.actionIndex ?? parseEdgeId(edge.id)?.actionIndex;
      setSelectedItem({
        type: 'edge',
        source: edge.source,
        target: edge.target,
        actionIndex,
        edgeId: edge.id,
      });
    },
    [setSelectedItem, lastClickedNodeRef]
  );

  const onPaneClick = useCallback(() => {
    editorSidebarRef.current?.commitDeselectAndSave?.();
  }, [editorSidebarRef]);

  return {
    nodes,
    edges,
    setNodes,
    setEdges,
    fitView,
    onNodesChange,
    handleEdgesChange,
    handleConnect,
    handleConnectEnd,
    handleWorkflowChange,
    onNodesDelete,
    onEdgesDelete,
    onNodeDrag,
    onNodeDragStop,
    handleDragOver,
    handleDrop,
    handleUploadJSON,
    handleAiStepsApplied,
    handleAutoLayout,
    onNodeClick,
    onEdgeClick,
    onPaneClick,
    defaultEdgeOptions,
    nodeTypes,
    edgeTypes,
  };
}
